import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, Send, Users, BarChart3, Settings, RefreshCw,
  CheckCircle, XCircle, Clock, Eye, Search, Filter, Phone,
  ChevronRight, AlertCircle, Loader2, ArrowUp, ArrowDown,
  MessageSquare, UserPlus, Zap, Calendar, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { color: 'bg-yellow-900/50 text-yellow-400 border-yellow-700', icon: Clock },
    sent: { color: 'bg-blue-900/50 text-blue-400 border-blue-700', icon: ArrowUp },
    delivered: { color: 'bg-green-900/50 text-green-400 border-green-700', icon: CheckCircle },
    read: { color: 'bg-purple-900/50 text-purple-400 border-purple-700', icon: Eye },
    failed: { color: 'bg-red-900/50 text-red-400 border-red-700', icon: XCircle },
  };

  const { color, icon: Icon } = config[status] || config.pending;

  return (
    <Badge variant="outline" className={color}>
      <Icon className="w-3 h-3 mr-1" />
      {status}
    </Badge>
  );
};

// Message item component
const MessageItem = ({ message, onClick }) => {
  const isOutbound = message.direction === 'outbound';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-lg cursor-pointer transition-colors ${
        isOutbound 
          ? 'bg-orange-900/20 border border-orange-800/50 hover:bg-orange-900/30'
          : 'bg-slate-700/50 border border-slate-600/50 hover:bg-slate-700'
      }`}
      onClick={() => onClick?.(message)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isOutbound ? (
              <ArrowUp className="w-4 h-4 text-orange-400" />
            ) : (
              <ArrowDown className="w-4 h-4 text-green-400" />
            )}
            <span className="font-medium text-white">
              {message.user_name || message.phone}
            </span>
            {message.user_name && (
              <span className="text-xs text-slate-500">({message.phone})</span>
            )}
          </div>
          <p className="text-sm text-slate-300 line-clamp-2">
            {message.content}
          </p>
          {message.trip_name && (
            <span className="text-xs text-slate-500 mt-1 block">
              Viagem: {message.trip_name}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={message.status} />
          <span className="text-xs text-slate-500">
            {new Date(message.created_at).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Stats card component
const StatsCard = ({ title, value, subtitle, icon: Icon, trend, color = 'orange' }) => {
  const colors = {
    orange: 'bg-orange-900/30 text-orange-400',
    green: 'bg-green-900/30 text-green-400',
    blue: 'bg-blue-900/30 text-blue-400',
    purple: 'bg-purple-900/30 text-purple-400',
    red: 'bg-red-900/30 text-red-400',
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {subtitle && (
              <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${
            trend >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
            <span>{Math.abs(trend)}% vs semana anterior</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Send message dialog
const SendMessageDialog = ({ isOpen, onClose, onSend, loading }) => {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (!phone || !message) {
      toast.error('Preencha todos os campos');
      return;
    }
    onSend({ phone, message });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-orange-500" />
            Enviar Mensagem
          </DialogTitle>
          <DialogDescription>
            Envie uma mensagem WhatsApp para um número específico
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Telefone</Label>
            <Input
              id="phone"
              placeholder="(11) 99999-9999"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="send-phone-input"
            />
            <p className="text-xs text-slate-500">
              Inclua DDD. Ex: 11999998888
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Mensagem</Label>
            <Textarea
              id="message"
              placeholder="Digite sua mensagem..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              data-testid="send-message-input"
            />
            <p className="text-xs text-slate-500">
              {message.length}/4096 caracteres
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="send-message-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Conversation view dialog
const ConversationDialog = ({ isOpen, onClose, phone, api }) => {
  const [messages, setMessages] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen && phone) {
      loadConversation();
    }
  }, [isOpen, phone]);

  const loadConversation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/whatsapp/messages/${phone}`);
      setMessages(response.data.messages);
      setUser(response.data.user);
    } catch (error) {
      toast.error('Erro ao carregar conversa');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;

    try {
      setSending(true);
      await api.post('/admin/whatsapp/messages/send', {
        phone,
        message: replyMessage
      });
      toast.success('Mensagem enviada!');
      setReplyMessage('');
      loadConversation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-green-500" />
            {user?.name || phone}
          </DialogTitle>
          {user && (
            <DialogDescription>
              {user.email} • {phone}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : (
          <>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      msg.direction === 'outbound'
                        ? 'bg-orange-900/30 text-white'
                        : 'bg-slate-700 text-slate-200'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <div className="flex items-center justify-end gap-2 mt-1">
                        <span className="text-xs text-slate-500">
                          {new Date(msg.created_at).toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {msg.direction === 'outbound' && (
                          <StatusBadge status={msg.status} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex gap-2 pt-4 border-t border-slate-700">
              <Input
                placeholder="Digite uma resposta..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                data-testid="reply-input"
              />
              <Button
                onClick={handleSendReply}
                disabled={sending || !replyMessage.trim()}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="reply-send-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminWhatsApp() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // Filters
  const [messageFilter, setMessageFilter] = useState('all');
  const [searchPhone, setSearchPhone] = useState('');
  
  // Dialogs
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [showConversation, setShowConversation] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [statsRes, messagesRes, conversationsRes, templatesRes] = await Promise.all([
        api.get('/admin/whatsapp/stats'),
        api.get('/admin/whatsapp/messages?limit=20'),
        api.get('/admin/whatsapp/conversations?limit=20'),
        api.get('/admin/whatsapp/templates')
      ]);

      setStats(statsRes.data.stats);
      setIsConfigured(statsRes.data.is_configured);
      setMessages(messagesRes.data.messages);
      setTotalMessages(messagesRes.data.total);
      setConversations(conversationsRes.data.conversations);
      setTemplates(templatesRes.data.templates);
    } catch (error) {
      console.error('Error loading WhatsApp data:', error);
      toast.error('Erro ao carregar dados do WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (filter = 'all') => {
    try {
      const params = new URLSearchParams({ limit: '50' });
      
      if (filter === 'sent') params.append('direction', 'outbound');
      if (filter === 'received') params.append('direction', 'inbound');
      if (filter === 'failed') params.append('status', 'failed');
      if (searchPhone) params.append('phone', searchPhone);

      const response = await api.get(`/admin/whatsapp/messages?${params}`);
      setMessages(response.data.messages);
      setTotalMessages(response.data.total);
    } catch (error) {
      toast.error('Erro ao filtrar mensagens');
    }
  };

  const handleSendMessage = async (data) => {
    try {
      setSendingMessage(true);
      const response = await api.post('/admin/whatsapp/messages/send', data);
      
      if (response.data.success) {
        toast.success('Mensagem enviada com sucesso!');
        setShowSendDialog(false);
        loadMessages();
        loadData();
      } else {
        toast.error(response.data.error || 'Erro ao enviar mensagem');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const response = await api.post('/admin/whatsapp/test-connection');
      
      if (response.data.success) {
        toast.success(
          <div>
            <p className="font-medium">Conexão OK!</p>
            {response.data.phone_info && (
              <p className="text-sm text-slate-400">
                {response.data.phone_info.display_phone_number} - {response.data.phone_info.verified_name}
              </p>
            )}
          </div>
        );
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Erro ao testar conexão');
    }
  };

  const handleMessageClick = (message) => {
    setSelectedPhone(message.phone);
    setShowConversation(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-green-500" />
            WhatsApp
          </h1>
          <p className="text-slate-400">
            Gerencie mensagens e conversas do WhatsApp
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={!isConfigured}
            className="border-slate-600"
            data-testid="test-connection-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            Testar Conexão
          </Button>
          <Button
            onClick={() => setShowSendDialog(true)}
            disabled={!isConfigured}
            className="bg-green-600 hover:bg-green-700"
            data-testid="send-message-dialog-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Nova Mensagem
          </Button>
        </div>
      </div>

      {/* Not configured warning */}
      {!isConfigured && (
        <Card className="bg-yellow-900/20 border-yellow-700">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertCircle className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="font-medium text-yellow-400">WhatsApp não configurado</p>
              <p className="text-sm text-yellow-400/70">
                Configure as credenciais do WhatsApp Cloud API em{' '}
                <a href="/admin/integrations" className="underline">Integrações</a>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <StatsCard
            title="Total de Mensagens"
            value={stats.total_messages}
            subtitle={`${stats.messages_today} hoje`}
            icon={MessageSquare}
            color="blue"
          />
          <StatsCard
            title="Enviadas"
            value={stats.messages_sent}
            icon={ArrowUp}
            color="orange"
          />
          <StatsCard
            title="Recebidas"
            value={stats.messages_received}
            icon={ArrowDown}
            color="green"
          />
          <StatsCard
            title="Entregues"
            value={stats.messages_delivered}
            icon={CheckCircle}
            color="purple"
          />
          <StatsCard
            title="Contatos"
            value={stats.unique_contacts}
            subtitle={`${stats.active_conversations} conversas ativas`}
            icon={Users}
            color="blue"
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="messages" className="space-y-4">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="messages" className="data-[state=active]:bg-slate-700">
            <MessageSquare className="w-4 h-4 mr-2" />
            Mensagens
          </TabsTrigger>
          <TabsTrigger value="conversations" className="data-[state=active]:bg-slate-700">
            <Users className="w-4 h-4 mr-2" />
            Conversas
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-slate-700">
            <Zap className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
        </TabsList>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Buscar por telefone..."
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadMessages(messageFilter)}
                className="pl-10 bg-slate-800 border-slate-700"
                data-testid="search-phone-input"
              />
            </div>
            <Select value={messageFilter} onValueChange={(v) => { setMessageFilter(v); loadMessages(v); }}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="sent">Enviadas</SelectItem>
                <SelectItem value="received">Recebidas</SelectItem>
                <SelectItem value="failed">Falhas</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => loadMessages(messageFilter)}
              className="border-slate-600"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Message list */}
          <div className="space-y-3">
            <AnimatePresence>
              {messages.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-8 text-center">
                    <MessageCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">Nenhuma mensagem encontrada</p>
                  </CardContent>
                </Card>
              ) : (
                messages.map((message) => (
                  <MessageItem
                    key={message.id}
                    message={message}
                    onClick={handleMessageClick}
                  />
                ))
              )}
            </AnimatePresence>
          </div>

          {totalMessages > messages.length && (
            <div className="text-center">
              <Button variant="outline" className="border-slate-600">
                Carregar mais ({totalMessages - messages.length} restantes)
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Conversations Tab */}
        <TabsContent value="conversations" className="space-y-4">
          <div className="grid gap-4">
            {conversations.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Nenhuma conversa encontrada</p>
                </CardContent>
              </Card>
            ) : (
              conversations.map((conv) => (
                <Card 
                  key={conv.id} 
                  className="bg-slate-800 border-slate-700 cursor-pointer hover:bg-slate-750 transition-colors"
                  onClick={() => { setSelectedPhone(conv.phone); setShowConversation(true); }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-900/30 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">
                            {conv.user_name || conv.phone}
                          </p>
                          {conv.user_name && (
                            <p className="text-xs text-slate-500">{conv.phone}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-slate-400">
                            {conv.message_count} mensagens
                          </p>
                          {conv.last_message_at && (
                            <p className="text-xs text-slate-500">
                              Última: {new Date(conv.last_message_at).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </div>
                        <Badge variant="outline" className={
                          conv.status === 'active' 
                            ? 'text-green-400 border-green-700'
                            : conv.status === 'waiting_response'
                              ? 'text-yellow-400 border-yellow-700'
                              : 'text-slate-500 border-slate-600'
                        }>
                          {conv.status === 'active' ? 'Ativa' : 
                           conv.status === 'waiting_response' ? 'Aguardando' : 
                           conv.status === 'completed' ? 'Concluída' : 'Expirada'}
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-slate-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <p className="text-slate-400 text-sm">
            Templates pré-definidos para notificações automáticas do ViajeJunto.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {templates.map((template) => (
              <Card key={template.id} className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-white">
                      {template.name}
                    </CardTitle>
                    <Badge variant="outline" className="text-slate-400 border-slate-600">
                      {template.category}
                    </Badge>
                  </div>
                  <CardDescription>{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs text-slate-400 whitespace-pre-wrap bg-slate-900 p-3 rounded-lg max-h-32 overflow-auto">
                    {template.message_template}
                  </pre>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <SendMessageDialog
        isOpen={showSendDialog}
        onClose={() => setShowSendDialog(false)}
        onSend={handleSendMessage}
        loading={sendingMessage}
      />

      <ConversationDialog
        isOpen={showConversation}
        onClose={() => setShowConversation(false)}
        phone={selectedPhone}
        api={api}
      />
    </div>
  );
}
